// @flow

import React, { Component } from 'react'
import Select, { components } from 'react-select'
import withTheme from 'hoc/withTheme'

import cn from 'classnames'

import styles from './Dropdown.module.scss'

import supportedThemes from '../themes/__supportedThemes.js'

type Props = {
    required?: boolean,
    disabled?: boolean,
    placeholder: ?string,
    options: Array<string> | { [value: string]: string },
    value?: string,
    defaultValue?: string,
    onChange: Function,
    trans?: string => string,
    optionFormat?: string
};

class Dropdown extends Component<Props> {
    static defaultProps = {
        required: true,
        disabled: false,
        placeholder: 'Select',
        optionFormat: '%name%'
    }

    constructor (props) {
        super(props)
        this.select = React.createRef()
    }

    get options (): { [string]: string } {
        if (!Array.isArray(this.props.options)) {
            return this.props.options
        }

        let options = {}

        this.props.options.forEach(item => {
            options[item] = this.formatOptionLabel(item)
        })

        return options
    }

    formatOptionLabel (label: string): string {
        label = this.props.optionFormat.replace('%name%', label)

        if (!this.props.trans) {
            return label
        }

        return this.props.trans(label)
    }

    render () {
        let {
            placeholder,
            value,
            defaultValue,
            onChange,
            theme
        }: Props = this.props

        let options = this.options
        let valueProp

        if (value) {
            valueProp = { value }
        } else {
            if (defaultValue) {
                valueProp = { defaultValue }
            } else {
                valueProp = { value: '' }
            }
        }

        const selectOptions = [
            placeholder ? { value: '', label: placeholder } : {},
            ...Object.keys(options).map(key => ({ value: key, label: options[key] }))
        ]

        return (
            <div className={cn(styles.dropdown, this.props.className)} style={{
                ...(this.props.style || {}),
                '--dropdown-color': theme.dropdownColor,
                '--field-background': theme.fieldBackground,
                '--primary-font': theme.primaryFont
            }}>
                <Select
                    {...valueProp}
                    components={{ DropdownIndicator }}
                    className="sh-select__container"
                    classNamePrefix="sh-select"
                    ref={this.select}
                    placeholder={placeholder || null}
                    required={this.props.required}
                    isDisabled={this.props.disabled}
                    isSearchable={false}
                    onFocus={this.props.onFocus || null}
                    onBlur={this.props.onBlur || null}
                    options={selectOptions}
                    onChange={selectedOption => onChange(selectedOption.value)}
                    defaultValue={selectOptions.find(op => op.value === this.props.defaultValue) || null}
                />
                <input
                    tabIndex={-1}
                    value={this.props.value || this.props.defaultValue}
                    required={this.props.required}
                    onChange={selectedOption => onChange(selectedOption.value)}
                    style={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        background: 'transparent',
                        border: 0,
                        pointerEvents: 'none',
                        color: 'transparent',
                        textIndent: '-9999px'
                    }}
                    onFocus={() => {
                        const controlRef = this.select.current.select?.controlRef
                        if (controlRef) {
                            controlRef.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        }
                        this.select.current.focus()
                    }}
                />
            </div>
        )
    }
}

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <ArrowDown width={`${14 / 16}em`} height={`${10 / 16}em`} />
        </components.DropdownIndicator>
    )
}

const ArrowDown = props => (
    <svg
        width={13}
        height={8}
        viewBox="0 0 13 8"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
    >
        <title>{'Path'}</title>
        <path
            d="M1 1.5l5.416 5.416L11.83 1.5"
            stroke="#000"
            strokeWidth={2}
            fill="none"
            fillRule="evenodd"
            strokeLinecap="round"
            strokeLinejoin="round"
      />
    </svg>
)

export default withTheme(supportedThemes)(Dropdown)
